import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useRef,
    useState
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FiActivity, FiSettings, FiLogIn, FiLogOut, FiUserPlus } from "react-icons/fi";
import { Motion } from "../../colors";

// Sidebar surface — deep midnight-violet, slightly darker than BrandHex[900]
// so it reads as ink rather than primary action.
const SIDEBAR_BG = "bg-[#1c1530]";
const SIDEBAR_HOVER = "hover:bg-[#2a2046]";
const SIDEBAR_ICON = "text-zinc-400 hover:text-white";
const SIDEBAR_DIVIDER = "border-[#34284f]";

import { useUserContext } from "../../contexts/user_context";
import { useNavigationContext } from "../../contexts/NavContext/nav-context";
import { useNotificationContext } from "../../contexts/notification_context";
import { useJobContext } from "../../contexts/job_context";

import Logo from "../Logo/Logo";
import JobProgressList from "../Jobs";
import {AppView, AppViewToLinkMap} from "../../App";
import { LayoutSizes, ZLevels } from "../../sizes";

// Context for controlling the expandable left panel
interface DashboardHeaderContextType {
    leftPanelWidth: number;
    setLeftPanelWidth: (w: number) => void;
    openLeftPanel: () => void;
    closeLeftPanel: () => void;
    expandLeftPanel: () => void;
    collapseLeftPanel: () => void;
    hoverOpenLeftPanel: () => void;  // Opens panel without "pinning" it
    isPinned: boolean;  // Whether panel is explicitly opened (pinned)
}

const DashboardHeaderContext = createContext<DashboardHeaderContextType | null>(null);

export const useDashboardHeader = () => {
    const ctx = useContext(DashboardHeaderContext);
    if (!ctx) throw new Error('useDashboardHeader must be used within DashboardHeader');
    return ctx;
};

export interface DashboardHeaderProps {
    /** Icons/buttons to render in the sidebar under the Logo */
    sidebarIcons?: React.ReactNode;
    /** Content to render in the expandable panel (right of sidebar) */
    panelContent?: React.ReactNode;
    /** Starting width for the left panel when opened (default 300) */
    defaultPanelWidth?: number;
    /** Main page content */
    children?: React.ReactNode;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({ sidebarIcons, panelContent, defaultPanelWidth, children }) => {
    const { logout, isLoggedIn } = useUserContext()!;
    const { navigateTo } = useNavigationContext()!;
    const { jobsOpen, setJobsOpen } = useNotificationContext()!;
    const { activeJobs } = useJobContext()!;
    const navigate = useNavigate();
    const location = useLocation();

    const jobsRef = useRef<HTMLDivElement | null>(null);

    // Left panel resize state
    const LEFT_PANEL_COLLAPSED = 0;
    const LEFT_PANEL_EXPANDED = defaultPanelWidth ?? 350;
    const MIN_PANEL_WIDTH = 10;

    const [leftPanelWidth, setLeftPanelWidth] = useState(LEFT_PANEL_COLLAPSED);
    const [isLeftLocked, setIsLeftLocked] = useState(true);
    const isResizingLeft = useRef(false);
    const stateRef = useRef({ leftPanelWidth, isLeftLocked });

    useEffect(() => {
        stateRef.current = { leftPanelWidth, isLeftLocked };
    }, [leftPanelWidth, isLeftLocked]);

    // Reset panel width on navigation
    useEffect(() => {
        setLeftPanelWidth(LEFT_PANEL_COLLAPSED);
        setIsLeftLocked(true);
        setIsExplicitlyOpened(false);
    }, [location.pathname]);

    const handleLogout = () => {
        logout();
        navigateTo({ view: AppView.Login });
    };

    // Close the job list when clicking outside
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (jobsRef.current && !jobsRef.current.contains(e.target as Node)) {
                setJobsOpen(false);
            }
        };
        if (jobsOpen) document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [jobsOpen]);

    // Track whether panel was explicitly opened via click (vs hover)
    const [isExplicitlyOpened, setIsExplicitlyOpened] = useState(false);
    const hasPanel = !!panelContent;

    // Panel control functions
    const openLeftPanel = useCallback(() => {
        setLeftPanelWidth(LEFT_PANEL_EXPANDED);
        setIsLeftLocked(false);
        setIsExplicitlyOpened(true);
    }, [LEFT_PANEL_EXPANDED]);

    const closeLeftPanel = useCallback(() => {
        setLeftPanelWidth(LEFT_PANEL_COLLAPSED);
        setIsLeftLocked(false);
        setIsExplicitlyOpened(false);
    }, []);

    // Opens panel without "pinning" it - for hover behavior on icons
    const hoverOpenLeftPanel = useCallback(() => {
        if (!isExplicitlyOpened) {
            setLeftPanelWidth(LEFT_PANEL_EXPANDED);
            setIsLeftLocked(false);
        }
    }, [isExplicitlyOpened, LEFT_PANEL_EXPANDED]);

    const handlePanelMouseLeave = useCallback(() => {
        if (!isExplicitlyOpened && leftPanelWidth > LEFT_PANEL_COLLAPSED) {
            setLeftPanelWidth(LEFT_PANEL_COLLAPSED);
        }
    }, [isExplicitlyOpened, leftPanelWidth]);

    const expandLeftPanel = useCallback(() => {
        const fullWidth = window.innerWidth - 200;
        setLeftPanelWidth(fullWidth);
        setIsLeftLocked(false);
    }, []);

    const collapseLeftPanel = useCallback(() => {
        setLeftPanelWidth(LEFT_PANEL_COLLAPSED);
        setIsLeftLocked(false);
    }, []);

    // Mouse move/resizing logic for left panel
    const handleMouseMove = useCallback((e: MouseEvent) => {
        e.preventDefault();
        const { isLeftLocked } = stateRef.current;

        if (isResizingLeft.current && !isLeftLocked) {
            // Subtract the dashboard header width (56px = w-14)
            const dashboardHeaderWidth = 56;
            const newWidth = Math.max(
                MIN_PANEL_WIDTH,
                Math.min(e.clientX - dashboardHeaderWidth, window.innerWidth - 200)
            );
            setLeftPanelWidth(newWidth);
        }
    }, []);

    const stopResizing = useCallback(() => {
        isResizingLeft.current = false;
    }, []);

    useEffect(() => {
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', stopResizing);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', stopResizing);
        };
    }, [handleMouseMove, stopResizing]);

    const handleLeftMouseDown = (e: React.MouseEvent) => {
        e.preventDefault();
        isResizingLeft.current = true;
    };

    const hasChildren = !!children;
    const hasSidebarIcons = !!sidebarIcons;
    const isPanelOpen = leftPanelWidth > 0;

    // Standalone sidebar mode (for LoginOrSignup page)
    if (!hasChildren && !hasPanel && !hasSidebarIcons) {
        return (
            <aside
                id="dashboard-header-anchor"
                className={`flex flex-col h-screen sticky top-0 ${ZLevels.above} ${LayoutSizes.dashboardHeaderClass} ${SIDEBAR_BG}`}
            >
                {/* --- Top Section: Logo --- */}
                <div className="flex flex-col items-center w-full py-5">
                    <div
                        onClick={() => navigateTo({ view: AppView.MyDashboard })}
                        className={`cursor-pointer hover:opacity-80 ${Motion.base}`}
                    >
                        <Logo />
                    </div>
                    <span className="mt-1 text-[9px] font-bold uppercase tracking-widest text-[#9c83cf]">Beta</span>
                </div>

                {/* --- Middle Spacer: Pushes content apart --- */}
                <div className="flex-grow w-full" />

                {/* --- Bottom Section: System Tools --- */}
                <div className={`flex flex-col items-center gap-3 w-full pb-6 ${SIDEBAR_ICON}`}>
                    <div className="relative" ref={jobsRef}>
                        <button
                            title="Job Progress"
                            className={`relative p-2 rounded-lg ${SIDEBAR_HOVER} ${Motion.fast} ${jobsOpen ? 'text-white bg-[#2a2046]' : ''}`}
                            onClick={() => setJobsOpen((prev) => !prev)}
                        >
                            <FiActivity className="w-[1.25em] h-[1.25em]" />
                            {activeJobs && activeJobs.length > 0 && !jobsOpen && (
                                <span className={`absolute top-1 right-1 w-2.5 h-2.5 rounded-full border-2 border-[#1c1530] ${
                                    activeJobs.some(j => j.warnings && j.warnings.length > 0)
                                        ? 'bg-amber-400'
                                        : 'bg-emerald-400 animate-pulse'
                                }`} />
                            )}
                        </button>

                        {jobsOpen && (
                            <div
                                className={`absolute bottom-0 left-14 bg-white text-zinc-800
                                ${ZLevels.above}
                                rounded-xl shadow-[0_12px_32px_-16px_rgba(60,40,110,0.28)] p-4 w-72 max-h-[70vh] overflow-y-auto
                                border border-[#e8e8ec] animate-in fade-in slide-in-from-left-2`}
                            >
                                <h4 className="text-xs font-semibold uppercase tracking-wider mb-3 text-zinc-500">
                                    Active Jobs
                                </h4>
                                <JobProgressList jobs={activeJobs ?? []} />
                            </div>
                        )}
                    </div>

                    <div className={`flex flex-col items-center gap-2 pt-3 mt-1 border-t ${SIDEBAR_DIVIDER} w-2/3`}>
                        {isLoggedIn ? (
                            <button
                                title="Logout"
                                className={`p-2 rounded-lg hover:text-red-400 ${SIDEBAR_HOVER} ${Motion.fast}`}
                                onClick={handleLogout}
                            >
                                <FiLogOut className="w-[1.25em] h-[1.25em]" />
                            </button>
                        ) : (
                            <>
                                <button
                                    title="Login"
                                    className={`p-2 rounded-lg ${SIDEBAR_HOVER} ${Motion.fast}`}
                                    onClick={() => navigate(AppViewToLinkMap[AppView.Login])}
                                >
                                    <FiLogIn className="w-[1.25em] h-[1.25em]" />
                                </button>
                                <button
                                    title="Create Account"
                                    className={`p-2 rounded-lg ${SIDEBAR_HOVER} ${Motion.fast}`}
                                    onClick={() => navigate(AppViewToLinkMap[AppView.SignUp])}
                                >
                                    <FiUserPlus className="w-[1.25em] h-[1.25em]" />
                                </button>
                            </>
                        )}
                    </div>

                    <footer className="text-[10px] text-zinc-500 pt-2">
                        © {new Date().getFullYear()}
                    </footer>
                </div>
            </aside>
        );
    }

    return (
        <DashboardHeaderContext.Provider
            value={{
                leftPanelWidth,
                setLeftPanelWidth,
                openLeftPanel,
                closeLeftPanel,
                expandLeftPanel,
                collapseLeftPanel,
                hoverOpenLeftPanel,
                isPinned: isExplicitlyOpened,
            }}
        >
            <div className="flex h-screen w-full">
                {/* Sidebar + Panel container for hover behavior */}
                <div
                    className="flex h-full"
                    onMouseLeave={handlePanelMouseLeave}
                >
                    {/* Fixed Dashboard Sidebar */}
                    <aside
                        id="dashboard-header-anchor"
                        className={`flex flex-col h-screen shrink-0 sticky top-0 ${ZLevels.above} w-14 ${SIDEBAR_BG}`}
                    >
                        <div className="flex flex-col items-center w-full py-5">
                            <div
                                onClick={() => navigateTo({ view: AppView.MyDashboard })}
                                className={`cursor-pointer hover:opacity-80 ${Motion.base}`}
                            >
                                <Logo />
                            </div>
                            <span className="mt-1 text-[9px] font-bold uppercase tracking-widest text-[#9c83cf]">Beta</span>
                        </div>

                        {hasSidebarIcons && (
                            <div className="flex flex-col items-center w-full">
                                {sidebarIcons}
                            </div>
                        )}

                        <div className="flex-grow w-full" />

                        <div className={`flex flex-col items-center gap-3 w-full pb-6 ${SIDEBAR_ICON}`}>
                            <div className="relative" ref={jobsRef}>
                                <button
                                    title="Job Progress"
                                    className={`p-2 rounded-lg ${SIDEBAR_HOVER} ${Motion.fast} ${jobsOpen ? 'text-white bg-[#2a2046]' : ''}`}
                                    onClick={() => setJobsOpen((prev) => !prev)}
                                >
                                    <FiActivity className="w-[1.25em] h-[1.25em]" />
                                </button>

                                {jobsOpen && (
                                    <div
                                        className={`absolute bottom-0 left-14 bg-white text-zinc-800
                                        ${ZLevels.above}
                                        rounded-xl shadow-[0_12px_32px_-16px_rgba(60,40,110,0.28)] p-4 w-72 max-h-[70vh] overflow-y-auto
                                        border border-[#e8e8ec] animate-in fade-in slide-in-from-left-2`}
                                    >
                                        <h4 className="text-xs font-semibold uppercase tracking-wider mb-3 text-zinc-500">
                                            Active Jobs
                                        </h4>
                                        <JobProgressList jobs={activeJobs ?? []} />
                                    </div>
                                )}
                            </div>

                            <div className={`flex flex-col items-center gap-2 pt-3 mt-1 border-t ${SIDEBAR_DIVIDER} w-2/3`}>
                                {isLoggedIn ? (
                                    <>
                                        <button
                                            title="Settings"
                                            className={`p-2 rounded-lg ${SIDEBAR_HOVER} ${Motion.fast}`}
                                            onClick={() => navigateTo({ view: AppView.UserSettings })}
                                        >
                                            <FiSettings className="w-[1.25em] h-[1.25em]" />
                                        </button>
                                        <button
                                            title="Logout"
                                            className={`p-2 rounded-lg hover:text-red-400 ${SIDEBAR_HOVER} ${Motion.fast}`}
                                            onClick={handleLogout}
                                        >
                                            <FiLogOut className="w-[1.25em] h-[1.25em]" />
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            title="Login"
                                            className={`p-2 rounded-lg ${SIDEBAR_HOVER} ${Motion.fast}`}
                                            onClick={() => navigate("/login")}
                                        >
                                            <FiLogIn className="w-[1.25em] h-[1.25em]" />
                                        </button>
                                        <button
                                            title="Create Account"
                                            className={`p-2 rounded-lg ${SIDEBAR_HOVER} ${Motion.fast}`}
                                            onClick={() => navigate("/sign-up")}
                                        >
                                            <FiUserPlus className="w-[1.25em] h-[1.25em]" />
                                        </button>
                                    </>
                                )}
                            </div>

                            <footer className="text-[10px] text-zinc-500 pt-2">
                                © {new Date().getFullYear()}
                            </footer>
                        </div>
                    </aside>

                    {/* Expandable Panel (for palette content, folder upload, etc.) */}
                    {hasPanel && isPanelOpen && (
                        <>
                            <div
                                className="overflow-visible h-full bg-white border-r border-[#e8e8ec]"
                                style={{ width: leftPanelWidth }}
                            >
                                {panelContent}
                            </div>

                            {!isLeftLocked && (
                                <div
                                    onMouseDown={handleLeftMouseDown}
                                    onDoubleClick={() => {
                                        if (leftPanelWidth <= 50) {
                                            expandLeftPanel();
                                        } else {
                                            collapseLeftPanel();
                                        }
                                    }}
                                    className={`w-1 bg-[#f4f4f6] cursor-col-resize hover:bg-[#bcabe0] ${Motion.fast}`}
                                />
                            )}
                        </>
                    )}
                </div>

                {/* Main Content Area */}
                <div className="flex-1 min-w-0 h-full overflow-hidden bg-[#fafafb]">
                    {children}
                </div>
            </div>
        </DashboardHeaderContext.Provider>
    );
};

export default DashboardHeader;
